import axios from 'axios';

/**
 * Executes a service call with a specific context that will be passed to axios interceptors.
 * This allows for context-specific handling of API requests without modifying the service methods.
 *
 * @param context The context identifier (e.g., 'sifflet-ai-chat')
 * @param serviceCall The service method to execute
 * @returns Promise that resolves to the service call result
 *
 */
export default async function callWithContext<T>(
  context: string,
  serviceCall: () => Promise<T>,
): Promise<T> {
  // Create a temporary interceptor that adds context to all requests and
  // add context to the meta property which gets picked up by the request handler
  const interceptor = axios.interceptors.request.use((config) => ({
    ...config,
    meta: { context },
  }));

  try {
    // Execute the service call with context applied
    return await serviceCall();
  } finally {
    // Remove the interceptor to avoid affecting other requests
    axios.interceptors.request.eject(interceptor);
  }
}
